// src/app/admin/kanban/Kanban.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';

import {
  Timestamp,
  writeBatch,
  arrayUnion,
  doc,
  collection,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '@root/firebase';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '@context/AuthContext';
import { useParams } from 'react-router-dom';

import Layout from '@components/Layout';
import Modal from '@components/Modal';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';

dayjs.extend(relativeTime);

/* =========================
   Constants / helpers
   ========================= */
const STATUSES = [
  { key: 'todo', title: 'To-Do List' },
  { key: 'in_progress', title: 'In Progress' },
  { key: 'review', title: 'Review' },
  { key: 'done', title: 'Done' },
];

function groupByStatus(tasks) {
  const map = { todo: [], in_progress: [], review: [], done: [] };
  tasks.forEach((t) => {
    const k = t.status || 'todo';
    if (!map[k]) map[k] = [];
    map[k].push(t);
  });
  Object.keys(map).forEach((k) =>
    map[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  );
  return map;
}

function ProgressBar({ pct = 0, className = '' }) {
  const clamp = Math.max(0, Math.min(100, pct));
  return (
    <div className='progress default-progress my-3'>
      <div
        className={`progress-bar progress-animated ${className}`}
        style={{ width: `${clamp}%`, height: 10 }}
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamp}
      >
        <span className='sr-only'>{clamp}% Complete</span>
      </div>
    </div>
  );
}

function AvatarStack({ users, max = 5 }) {
  const shown = users.slice(0, max);
  const extra = Math.max(0, users.length - shown.length);
  return (
    <ul className='kanbanimg me-3 mb-3'>
      {shown.map((u) => (
        <li key={u.id} title={u.name || u.email}>
          <img
            src={u.avatar || '/assets/images/user.png'}
            alt={u.name || 'Admin'}
          />
        </li>
      ))}
      {extra > 0 && (
        <li>
          <span>{extra}+</span>
        </li>
      )}
    </ul>
  );
}

// Close bootstrap modal by id (fallback-safe)
function closeModalById(id) {
  try {
    const el = document.getElementById(id);
    if (!el) return;
    const bs = window.bootstrap?.Modal;
    if (bs) {
      const inst = bs.getInstance(el) || new bs(el);
      inst.hide();
      return;
    }
    el.classList.remove('show');
    el.setAttribute('aria-hidden', 'true');
    el.style.display = 'none';
    document.body.classList.remove('modal-open');
    document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
  } catch (_) {}
}

/** Derive transition fields on lane change (client timestamps) */
function computeTransitionUpdate(task, from, to, currentUser) {
  const now = Timestamp.now();
  const update = {
    status: to,
    movedBy: currentUser?.uid || null,
    lastClientMovedAt: now,
  };

  const toLower = to || 'todo';
  const fromLower = from || task?.status || 'todo';

  if (!task?.startedAt && toLower !== 'todo') {
    update.startedAt = now;
  }
  if (fromLower === 'done' && toLower !== 'done') {
    update.completedAt = null;
  }

  switch (toLower) {
    case 'todo':
      update.progressPct = 0;
      break;
    case 'in_progress':
      update.progressPct = fromLower === 'done' ? 50 : 25;
      break;
    case 'review':
      update.progressPct = 90;
      break;
    case 'done':
      update.progressPct = 100;
      update.completedAt = now;
      break;
    default:
      update.progressPct = task?.progressPct ?? 0;
  }
  return update;
}

/* =========================
   Component
   ========================= */
export default function Kanban() {
  const { projectId = 'smartdog' } = useParams();
  const { currentUser } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create modal state
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueAt: '',
    priority: 'medium',
    area: 'Development',
    status: 'todo',
    assignees: [], // <-- multi-select values (admin user IDs)
    tagAllAdmins: true, // <-- if true, ignore assignees and tag all admins
  });
  const onFormChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  /* ----- Firestore bindings ----- */
  useEffect(() => {
    const qy = query(
      collection(db, 'projects', projectId, 'tasks'),
      orderBy('order', 'asc'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTasks(list);
      setLoading(false);
    });
    return unsub;
  }, [projectId]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'projects', projectId), (snap) => {
      setProject(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return unsub;
  }, [projectId]);

  useEffect(() => {
    (async () => {
      const qy = query(
        collection(db, 'users'),
        where('isAdmin', '==', true)
        //where('active', '==', true)
      );
      const ss = await getDocs(qy);
      setAdmins(ss.docs.map((d) => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  const columns = useMemo(() => groupByStatus(tasks), [tasks]);
  const totalIn = (k) => columns[k]?.length || 0;

  /* ----- Create Task (modal confirm) ----- */
  const handleSubmit = useCallback(async () => {
    if (!form.title.trim()) {
      alert('Please add a title');
      return;
    }

    try {
      setIsSaving(true);

      const list = form.tagAllAdmins ? admins.map((a) => a.id) : form.assignees;
      const statusKey = form.status || 'todo';
      const nextOrder = (columns[statusKey]?.length || 0) + 1;

      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        dueAt: form.dueAt ? Timestamp.fromDate(new Date(form.dueAt)) : null,
        priority: form.priority,
        area: form.area,
        status: statusKey,
        assignees: list,
        createdBy: currentUser?.uid || null,
        createdAt: Timestamp.now(),
        order: nextOrder,
        progressPct: 0,
      };

      await addDoc(collection(db, 'projects', projectId, 'tasks'), payload);

      setForm({
        title: '',
        description: '',
        dueAt: '',
        priority: 'medium',
        area: 'Development',
        status: 'todo',
        assignees: [],
        tagAllAdmins: true,
      });

      closeModalById('createTask');
    } catch (err) {
      console.error(err);
      alert('Failed to create task. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [form, admins, currentUser, columns, projectId]);

  /* ----- Manual move via dropdown ----- */
  const moveTask = useCallback(
    async (task, nextStatus) => {
      if (task.status === nextStatus) return;
      const nextOrder = (columns[nextStatus]?.length || 0) + 1;
      await updateDoc(doc(db, 'projects', projectId, 'tasks', task.id), {
        status: nextStatus,
        order: nextOrder,
        updatedAt: Timestamp.now(),
        lastMovedAt: Timestamp.now(),
      });
    },
    [projectId, columns]
  );

  /* ----- Optimistic local move (prevents snap-back) ----- */
  const optimisticMove = useCallback(
    (taskId, fromStatus, toStatus, newIndex) => {
      setTasks((prev) => {
        const map = groupByStatus(prev);
        const fromArr = (map[fromStatus] || []).filter((t) => t.id !== taskId);
        const toArr = [...(map[toStatus] || [])];

        const moving = prev.find((t) => t.id === taskId);
        if (!moving) return prev;

        const updated = { ...moving, status: toStatus, 'aria-grabbed': true };
        const insertAt = Math.max(
          0,
          Math.min(newIndex ?? toArr.length, toArr.length)
        );
        toArr.splice(insertAt, 0, updated);

        const reNum = (arr) => arr.map((t, i) => ({ ...t, order: i + 1 }));

        map[fromStatus] = reNum(fromArr);
        map[toStatus] = reNum(toArr);

        return [...map.todo, ...map.in_progress, ...map.review, ...map.done];
      });
    },
    []
  );

  /* ----- Listen for global 'kanban:drop' from draggable.init.js ----- */
  const persistDrop = useCallback(
    async ({ taskId, fromColumn, toColumn, newIndex }) => {
      // 1) Optimistic update
      optimisticMove(taskId, fromColumn || 'todo', toColumn, newIndex);

      // 2) Persist batch: set dragged task + re-number dest (and source if changed)
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      const batch = writeBatch(db);

      // Normalize destination column ordering based on current DOM
      const destSelector =
        `.draggable-zone.dropzoneContainer[data-column="${toColumn}"],` +
        `[data-kanban-column="${toColumn}"]`;
      const destContainer = document.querySelector(destSelector);
      const destSiblings = destContainer
        ? Array.from(destContainer.querySelectorAll('.kanban-card'))
        : [];

      destSiblings.forEach((el, idx) => {
        const id = el.dataset.id;
        if (!id) return;
        const ref = doc(db, 'projects', projectId, 'tasks', id);

        if (id === taskId) {
          const transition = computeTransitionUpdate(
            task,
            fromColumn,
            toColumn,
            currentUser
          );
          batch.update(ref, {
            ...transition,
            updatedAt: Timestamp.now(),
            lastMovedAt: Timestamp.now(),
            order: idx + 1,
            statusHistory: arrayUnion({
              from: fromColumn || task?.status || null,
              to: toColumn,
              by: currentUser?.uid || null,
              at: Timestamp.now(),
            }),
          });
        } else {
          batch.update(ref, { order: idx + 1 });
        }
      });

      // Re-number source column if different
      if (fromColumn && fromColumn !== toColumn) {
        const srcSelector =
          `.draggable-zone.dropzoneContainer[data-column="${fromColumn}"],` +
          `[data-kanban-column="${fromColumn}"]`;
        const srcContainer = document.querySelector(srcSelector);
        const srcSiblings = srcContainer
          ? Array.from(srcContainer.querySelectorAll('.kanban-card'))
          : [];
        srcSiblings.forEach((el, idx) => {
          const id = el.dataset.id;
          if (!id) return;
          const ref = doc(db, 'projects', projectId, 'tasks', id);
          batch.update(ref, { order: idx + 1 });
        });
      }

      try {
        await batch.commit();
      } catch (err) {
        console.error('DnD persist failed:', err);
      }
    },
    [optimisticMove, tasks, projectId, currentUser]
  );

  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail) return;
      persistDrop(e.detail);
    };
    window.addEventListener('kanban:drop', handler);
    return () => window.removeEventListener('kanban:drop', handler);
  }, [persistDrop]);

  /* ----- UI helpers ----- */
  const formatCreated = useMemo(() => {
    const created = project?.createdAt?.toDate
      ? project.createdAt.toDate()
      : null;
    return created ? dayjs(created).format('MMM D, YYYY') : '—';
  }, [project]);

  // Multi-select change handler for admins
  const onAssigneesChange = useCallback((e) => {
    const values = Array.from(e.target.selectedOptions).map((o) => o.value);
    onFormChange('assignees', values);
  }, []);

  return (
    <Layout isAdminPage={true}>
      <div className='row'>
        <div className='col-xl-12'>
          <div className='row'>
            <div className='col-xl-12'>
              <div className='card overflow-hidden'>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-xl-8'>
                      <div>
                        <h4 className='fs-32 font-w700'>
                          {project?.name || 'SmartDog Project Tasks'}
                        </h4>
                      </div>
                    </div>

                    {/* RIGHT: Admin avatars + count */}
                    <div className='col-xl-4 info'>
                      <div className='d-flex justify-content-end align-items-center'>
                        <AvatarStack users={admins} />
                        <span className='ms-2 text-muted'>
                          {admins.length} Admins
                        </span>
                      </div>

                      <div className='justify-content-end d-flex align-items-center'></div>
                    </div>
                  </div>
                </div>
                {/* /card-body */}
              </div>
            </div>
          </div>

          {/* Create Task Modal */}
          <Modal
            id='createTask'
            title='Create Task'
            onConfirm={handleSubmit}
            confirmText='Create Task'
            loading={isSaving}
            loadingText='Creating…'
            size='lg'
          >
            <div className='row g-3'>
              <div className='col-12 form-floating'>
                <input
                  placeholder='title'
                  className={`form-control ${
                    !form.title.trim() && isSaving ? 'is-invalid' : ''
                  }`}
                  value={form.title}
                  onChange={(e) => onFormChange('title', e.target.value)}
                />
                <label className='form-label'>Title</label>
                {!form.title.trim() && isSaving && (
                  <div className='invalid-feedback'>Please add a title.</div>
                )}
              </div>

              <div className='col-12 form-floating'>
                <textarea
                  placeholder='title'
                  className='form-control'
                  rows={3}
                  value={form.description}
                  onChange={(e) => onFormChange('description', e.target.value)}
                />
                <label className='form-label'>Description</label>
              </div>

              <div className='col-md-4 form-floating'>
                <input
                  type='date'
                  className='form-control'
                  placeholder=''
                  value={form.dueAt}
                  onChange={(e) => onFormChange('dueAt', e.target.value)}
                />
                <label className='form-label'>Due date</label>
              </div>

              <div className='col-md-4 form-floating'>
                <select
                  className='form-select native-select'
                  placeholder=''
                  value={form.priority}
                  onChange={(e) => onFormChange('priority', e.target.value)}
                >
                  <option value='low'>Low</option>
                  <option value='medium'>Medium</option>
                  <option value='high'>High</option>
                </select>
                <label className='form-label'>Priority</label>
              </div>

              <div className='col-md-4 form-floating'>
                <select
                  className='form-select native-select'
                  placeholder=''
                  value={form.area}
                  onChange={(e) => onFormChange('area', e.target.value)}
                >
                  <option>Development</option>
                  <option>BAU / Operations</option>
                  <option>UX & Content</option>
                  <option>Data</option>
                  <option>Security</option>
                  <option>Governance</option>
                </select>
                <label className='form-label'>Area / Topic</label>
              </div>

              {/* Assign to all admins switch (keeps styling minimal) */}
              <div className='col-12'>
                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='assignAllAdmins'
                    checked={form.tagAllAdmins}
                    onChange={(e) =>
                      onFormChange('tagAllAdmins', e.target.checked)
                    }
                  />
                  <label className='form-check-label' htmlFor='assignAllAdmins'>
                    Assign to all admins
                  </label>
                </div>
              </div>

              {/* Assignees (admins) - MULTI SELECT (disabled if tagAllAdmins) */}
              <div className='col-md-6 form-floating'>
                <select
                  className='form-select native-select'
                  multiple
                  placeholder=''
                  value={form.assignees}
                  onChange={onAssigneesChange}
                  disabled={form.tagAllAdmins}
                  aria-label='Assign Owner(s)'
                >
                  {admins.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || a.email || a.id}
                    </option>
                  ))}
                </select>
                <label className='form-label'>Assign Owner(s)</label>
                <small className='text-muted d-block mt-1'>
                  {form.tagAllAdmins
                    ? 'All admins will be assigned.'
                    : `Selected: ${form.assignees.length || 0}`}
                </small>
              </div>

              <div className='col-md-6 form-floating'>
                <select
                  className='form-select'
                  placeholder=''
                  value={form.status}
                  onChange={(e) => onFormChange('status', e.target.value)}
                >
                  <option value='todo'>To-Do</option>
                  <option value='in_progress'>In Progress</option>
                  <option value='review'>Review</option>
                  <option value='done'>Done</option>
                </select>
                <label className='form-label'>Status</label>
              </div>
            </div>
          </Modal>

          {/* KANBAN */}
          <div className='row kanban-bx'>
            {STATUSES.map((col) => (
              <div className='col' key={col.key}>
                <div className='kanbanPreview-bx'>
                  <div
                    className='draggable-zone dropzoneContainer'
                    data-column={col.key}
                    data-kanban-column={col.key}
                  >
                    <div className='sub-card align-items-center d-flex justify-content-between mb-4'>
                      <div>
                        <h4 className='fs-20 mb-0 font-w600'>
                          {col.title} (
                          <span className='totalCount'>{totalIn(col.key)}</span>
                          )
                        </h4>
                      </div>
                      <div className='plus-bx'>
                        <button
                          type='button'
                          className='btn btn-link p-0'
                          data-bs-toggle='modal'
                          data-bs-target='#createTask'
                          aria-label='Add task'
                          title='Add task'
                        >
                          <i className='fas fa-plus'></i>
                        </button>
                      </div>
                    </div>

                    {(columns[col.key] || []).map((task) => (
                      <div
                        className='card draggable-handle draggable kanban-card'
                        data-id={task.id}
                        key={`${task.id}-${task.status}-${task.order ?? 0}`}
                        role='button'
                        aria-grabbed='false'
                        onMouseDown={(e) =>
                          e.currentTarget.setAttribute('aria-grabbed', 'true')
                        }
                        onMouseUp={(e) =>
                          e.currentTarget.setAttribute('aria-grabbed', 'false')
                        }
                      >
                        <div className='card-body'>
                          {/* Card header: title on the left, actions on the right */}
                          <div className='d-flex justify-content-between align-items-center mb-2'>
                            <p className='font-w600 fs-18 mb-0'>{task.title}</p>

                            <div className='tooltip-container'>
                              <div className='dropdown'>
                                <button
                                  type='button'
                                  className='reaction-btn reaction-action'
                                  data-bs-toggle='dropdown'
                                  aria-expanded='false'
                                  aria-label='Actions'
                                  title='Actions'
                                >
                                  <svg
                                    width='13'
                                    height='13'
                                    viewBox='0 0 24 24'
                                  >
                                    <g fill='none' fillRule='evenodd'>
                                      <rect width='24' height='24'></rect>
                                      <circle
                                        fill='#000'
                                        cx='5'
                                        cy='12'
                                        r='2'
                                      ></circle>
                                      <circle
                                        fill='#000'
                                        cx='12'
                                        cy='12'
                                        r='2'
                                      ></circle>
                                      <circle
                                        fill='#000'
                                        cx='19'
                                        cy='12'
                                        r='2'
                                      ></circle>
                                    </g>
                                  </svg>
                                </button>
                                <div className='dropdown-menu dropdown-menu-end'>
                                  {STATUSES.filter(
                                    (s) => s.key !== task.status
                                  ).map((s) => (
                                    <button
                                      key={s.key}
                                      className='dropdown-item'
                                      onClick={() => moveTask(task, s.key)}
                                    >
                                      Move to {s.title}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <span className='tooltip'>Actions</span>
                            </div>
                          </div>

                          <div>
                            <p>
                              <ReadMoreTextBase
                                text={task.description}
                                wordLimit={5}
                                readMore={true}
                                plainText={true}
                              />
                            </p>
                          </div>

                          <ProgressBar
                            pct={task.progressPct || 0}
                            className={
                              task.status === 'done'
                                ? 'bg-success'
                                : task.status === 'review'
                                ? 'bg-info'
                                : task.status === 'in_progress'
                                ? 'bg-warning'
                                : 'bg-design'
                            }
                          />

                          <div className='row justify-content-between align-items-center kanban-user'>
                            <ul className='users col-6'>
                              {(task.assignees || []).slice(0, 3).map((uid) => {
                                const user =
                                  admins.find((a) => a.id === uid) || {};
                                return (
                                  <li key={uid}>
                                    <img
                                      src={
                                        user.avatar || '/assets/images/user.png'
                                      }
                                      alt={user.name || 'User'}
                                    />
                                  </li>
                                );
                              })}
                            </ul>
                            <div className='col-6 d-flex justify-content-end'>
                              <span className='fs-14'>
                                <i className='far fa-clock me-2'></i>
                                {task.dueAt?.toDate
                                  ? dayjs(task.dueAt.toDate()).fromNow()
                                  : 'No due'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {!loading && totalIn(col.key) === 0 && (
                      <div className='text-muted small'>No tasks</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* /row kanban-bx */}
        </div>
      </div>
    </Layout>
  );
}
